"use client";

// src/useBaseQuery.ts
import "core-js/modules/es.promise.finally.js";
import * as React from "react";
import { isServer, noop, notifyManager } from "@tanstack/query-core";
import { useQueryClient } from "./QueryClientProvider.js";
import { useQueryErrorResetBoundary } from "./QueryErrorResetBoundary.js";
import { ensurePreventErrorBoundaryRetry, getHasError, useClearResetErrorBoundary } from "./errorBoundaryUtils.js";
import { useIsRestoring } from "./IsRestoringProvider.js";
import { ensureSuspenseTimers, fetchOptimistic, shouldSuspend, willFetch } from "./suspense.js";
function useBaseQuery(options, Observer, queryClient) {
  var _client$getDefaultOpt, _client$getDefaultOpt2, _client$getDefaultOpt3, _client$getDefaultOpt4;
  if (process.env.NODE_ENV !== "production") {
    if (typeof options !== "object" || Array.isArray(options)) {
      throw new Error('Bad argument type. Starting with v5, only the "Object" form is allowed when calling query related functions. Please use the error stack to find the culprit call. More info here: https://tanstack.com/query/latest/docs/react/guides/migrating-to-v5#supports-a-single-signature-one-object');
    }
  }
  const isRestoring = useIsRestoring();
  const errorResetBoundary = useQueryErrorResetBoundary();
  const client = useQueryClient(queryClient);
  const defaultedOptions = client.defaultQueryOptions(options);
  (_client$getDefaultOpt = client.getDefaultOptions().queries) === null || _client$getDefaultOpt === void 0 || (_client$getDefaultOpt2 = _client$getDefaultOpt._experimental_beforeQuery) === null || _client$getDefaultOpt2 === void 0 || _client$getDefaultOpt2.call(_client$getDefaultOpt, defaultedOptions);
  if (process.env.NODE_ENV !== "production") {
    if (!defaultedOptions.queryFn) {
      console.error("[".concat(defaultedOptions.queryHash, "]: No queryFn was passed as an option, and no default queryFn was found. The queryFn parameter is only optional when using a default queryFn. More info here: https://tanstack.com/query/latest/docs/framework/react/guides/default-query-function"));
    }
  }
  defaultedOptions._optimisticResults = isRestoring ? "isRestoring" : "optimistic";
  ensureSuspenseTimers(defaultedOptions);
  ensurePreventErrorBoundaryRetry(defaultedOptions, errorResetBoundary);
  useClearResetErrorBoundary(errorResetBoundary);
  const isNewCacheEntry = !client.getQueryCache().get(defaultedOptions.queryHash);
  const [observer] = React.useState(() => new Observer(client, defaultedOptions));
  const result = observer.getOptimisticResult(defaultedOptions);
  const shouldSubscribe = !isRestoring && options.subscribed !== false;
  React.useSyncExternalStore(React.useCallback(onStoreChange => {
    const unsubscribe = shouldSubscribe ? observer.subscribe(notifyManager.batchCalls(onStoreChange)) : noop;
    observer.updateResult();
    return unsubscribe;
  }, [observer, shouldSubscribe]), () => observer.getCurrentResult(), () => observer.getCurrentResult());
  React.useEffect(() => {
    observer.setOptions(defaultedOptions);
  }, [defaultedOptions, observer]);
  if (shouldSuspend(defaultedOptions, result)) {
    throw fetchOptimistic(defaultedOptions, observer, errorResetBoundary);
  }
  if (getHasError({
    result,
    errorResetBoundary,
    throwOnError: defaultedOptions.throwOnError,
    query: client.getQueryCache().get(defaultedOptions.queryHash),
    suspense: defaultedOptions.suspense
  })) {
    throw result.error;
  }
  ;
  (_client$getDefaultOpt3 = client.getDefaultOptions().queries) === null || _client$getDefaultOpt3 === void 0 || (_client$getDefaultOpt4 = _client$getDefaultOpt3._experimental_afterQuery) === null || _client$getDefaultOpt4 === void 0 || _client$getDefaultOpt4.call(_client$getDefaultOpt3, defaultedOptions, result);
  if (defaultedOptions.experimental_prefetchInRender && !isServer && willFetch(result, isRestoring)) {
    var _client$getQueryCache;
    const promise = isNewCacheEntry ?
    // Fetch immediately on render in order to ensure `.promise` is resolved even if the component is unmounted
    fetchOptimistic(defaultedOptions, observer, errorResetBoundary) : // subscribe to the "cache promise" so that we can finalize the currentThenable once data comes in
    (_client$getQueryCache = client.getQueryCache().get(defaultedOptions.queryHash)) === null || _client$getQueryCache === void 0 ? void 0 : _client$getQueryCache.promise;
    promise === null || promise === void 0 || promise.catch(noop).finally(() => {
      observer.updateResult();
    });
  }
  return !defaultedOptions.notifyOnChangeProps ? observer.trackResult(result) : result;
}
export { useBaseQuery };